import React, { useContext, useState } from 'react';
import * as Styled from './styled';
import { useSelector } from 'react-redux';
import { ResponsivePie } from '@nivo/pie';
import Modal from '@material-ui/core/Modal';
import {
    getScarvesCount,
    getCountryCount,
    getContributorsCount,
    getContributorsPieData,
    getContributorsListData,
} from '@containers/home/header/selectors';
import Flag from '@components/flag';
import { ThemeContext } from '@src/index';
import GlossyCarousel from '@components/glossyCarousel';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const images = [
    {
        url: 'https://api.mamikonyan.io/assets/carousel/7.jpg',
        text: 'Arena Lublin | 2025 | Lublin',
        iso3: 'POL',
    },
    {
        url: 'https://api.mamikonyan.io/assets/carousel/1.jpg',
        text: 'Emirates Stadium | 2023 | London',
        iso3: 'ENG',
    },
    {
        url: 'https://api.mamikonyan.io/assets/carousel/2.jpg',
        text: 'Cardiff City Stadium | 2023 | Cardiff',
        iso3: 'WAL',
    },
    {
        url: 'https://api.mamikonyan.io/assets/carousel/3.jpg',
        text: 'Camp Nou | 2021 | Barcelona',
        iso3: 'ESP',
    },
    {
        url: 'https://api.mamikonyan.io/assets/carousel/4.jpg',
        text: 'Gazprom Arena | 2021 | Saint Petersburg',
        iso3: 'RUS',
    },
    {
        url: 'https://api.mamikonyan.io/assets/carousel/5.jpg',
        text: 'MHPArena | 2019 | Stuttgart',
        iso3: 'DEU',
    },
    {
        url: 'https://api.mamikonyan.io/assets/carousel/6.png',
        text: 'Olympic Stadium | 2019 | Athens',
        iso3: 'GRC',
    },
    {
        url: 'https://api.mamikonyan.io/assets/carousel/8.png',
        text: 'Boris Paichadze Dinamo Arena | 2015 | Tbilisi',
        iso3: 'GEO',
    },
    {
        url: 'https://api.mamikonyan.io/assets/carousel/9.jpg',
        text: 'Vasil Levski National Stadium | 2012 | Sofia',
        iso3: 'BGR',
    },
];

const stringToColor = (str, isDarkMode) => {
    if (str === 'others') {
        return isDarkMode ? '#4a4a4a' : '#8a8a8a';
    }
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }

    const hue = Math.abs(hash) % 360;
    const saturation = isDarkMode ? 70 : 60;
    const lightness = isDarkMode ? 60 : 50;

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

const Header = () => {
    const palette = useContext(ThemeContext);
    const {
        PRIMARY_BG_COLOR,
        PRIMARY_FONT_COLOR,
        CARD_BORDER_COLOR,
        isDarkMode,
    } = palette;

    const scarvesCount = useSelector(getScarvesCount);
    const countryCount = useSelector(getCountryCount);
    const contributorsCount = useSelector(
        getContributorsCount
    );
    const pieData = useSelector(getContributorsPieData);
    const listData = useSelector(getContributorsListData);

    const topContributors = [...listData]
        .sort((a, b) => b.value - a.value)
        .filter(({ id }) => {
            return id !== 'paruyrmamikonyan';
        })
        .slice(0, 5);

    const totalScarves = pieData.reduce(
        (sum, d) => sum + d.value,
        0
    );

    const [open, setOpen] = useState(false);
    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    return (
        <Styled.HeaderWrapper>
            <Styled.HeaderContent>
                <Styled.HeaderTop>
                    <Styled.CarouselWrapper>
                        <GlossyCarousel images={images} />
                    </Styled.CarouselWrapper>

                    <Styled.HeaderText
                        $PRIMARY_FONT_COLOR={
                            palette.PRIMARY_FONT_COLOR
                        }
                        $CARD_BACKGROUND_COLOR={
                            palette.CARD_BACKGROUND_COLOR
                        }
                        $CARD_BORDER_COLOR={
                            palette.CARD_BORDER_COLOR
                        }
                        $isDarkMode={palette.isDarkMode}
                    >
                        Greetings! I'm Paruyr, hailing from
                        Yerevan, Armenia 🇦🇲
                        <br />I started collecting football
                        scarves from 2007. Currently, I have{' '}
                        <strong>{scarvesCount}</strong>{' '}
                        scarves from{' '}
                        <strong>{countryCount}</strong>{' '}
                        different countries 🌍
                        <br />
                        <strong>
                            {contributorsCount}
                        </strong>{' '}
                        people (including myself) so far
                        contributed to my collection by
                        gifting or exchanging scarves 🙌🏻
                        <br />
                        If you want to exchange (or gift)
                        scarves, feel free to reach out via{' '}
                        <Styled.HeaderTGLink
                            target="_blank"
                            href="https://t.me/mam1konyan"
                        >
                            Telegram
                        </Styled.HeaderTGLink>
                        ,{' '}
                        <Styled.HeaderIGLink
                            target="_blank"
                            href="https://www.instagram.com/mam1konyan/"
                        >
                            Instagram
                        </Styled.HeaderIGLink>{' '}
                        or{' '}
                        <Styled.HeaderFBLink
                            target="_blank"
                            href="https://www.facebook.com/mam1konyan"
                        >
                            Facebook
                        </Styled.HeaderFBLink>
                        🍻
                    </Styled.HeaderText>
                </Styled.HeaderTop>

                <Styled.ContributorsSection>
                    <Styled.ContributorsLeft>
                        <h2>Top 5 Contributors</h2>
                        <Styled.ContributorsList>
                            {topContributors.map(
                                (c, idx) => (
                                    <Styled.ContributorItem
                                        key={c.id}
                                        $rank={idx}
                                        $CARD_BORDER_COLOR={
                                            palette.CARD_BORDER_COLOR
                                        }
                                        $isDarkMode={
                                            palette.isDarkMode
                                        }
                                    >
                                        <Styled.ContributorRank
                                            $rank={idx}
                                        >
                                            {idx + 1}.
                                        </Styled.ContributorRank>
                                        <Styled.ContributorInfo>
                                            <Flag
                                                iso3={
                                                    c.country
                                                }
                                                size={
                                                    idx < 3
                                                        ? 24
                                                        : 18
                                                }
                                            />
                                            <Styled.ContributorName
                                                $rank={idx}
                                                $PRIMARY_FONT_COLOR={
                                                    palette.PRIMARY_FONT_COLOR
                                                }
                                            >
                                                {c.label}
                                            </Styled.ContributorName>
                                        </Styled.ContributorInfo>
                                        <Styled.ContributorValue
                                            $rank={idx}
                                        >
                                            {c.value}{' '}
                                            {c.value === 1
                                                ? 'scarf'
                                                : 'scarves'}
                                        </Styled.ContributorValue>
                                    </Styled.ContributorItem>
                                )
                            )}
                        </Styled.ContributorsList>

                        <Styled.ShowAllButton
                            onClick={handleOpen}
                            $CARD_BACKGROUND_COLOR={
                                palette.CARD_BACKGROUND_COLOR
                            }
                            $PRIMARY_FONT_COLOR={
                                palette.PRIMARY_FONT_COLOR
                            }
                            $CARD_BORDER_COLOR={
                                palette.CARD_BORDER_COLOR
                            }
                            $isDarkMode={palette.isDarkMode}
                        >
                            Show all contributors
                        </Styled.ShowAllButton>

                        <Modal
                            open={open}
                            onClose={(event, reason) => {
                                if (
                                    reason !==
                                    'backdropClick'
                                ) {
                                    handleClose();
                                }
                            }}
                            disableEscapeKeyDown
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <Styled.ModalContent
                                $MODAL_BACKGROUND_COLOR={
                                    palette.MODAL_BACKGROUND_COLOR
                                }
                                $CARD_BORDER_COLOR={
                                    palette.CARD_BORDER_COLOR
                                }
                                $PRIMARY_FONT_COLOR={
                                    palette.PRIMARY_FONT_COLOR
                                }
                                $isDarkMode={
                                    palette.isDarkMode
                                }
                            >
                                <Styled.ModalHeader
                                    $MODAL_BACKGROUND_COLOR={
                                        palette.MODAL_BACKGROUND_COLOR
                                    }
                                    $CARD_BORDER_COLOR={
                                        palette.CARD_BORDER_COLOR
                                    }
                                >
                                    <Styled.ModalTitle
                                        $PRIMARY_FONT_COLOR={
                                            palette.PRIMARY_FONT_COLOR
                                        }
                                    >
                                        All Contributors (
                                        {contributorsCount})
                                    </Styled.ModalTitle>
                                    <Styled.CloseButton
                                        $CARD_BACKGROUND_COLOR={
                                            palette.CARD_BACKGROUND_COLOR
                                        }
                                        $CARD_BORDER_COLOR={
                                            palette.CARD_BORDER_COLOR
                                        }
                                        $PRIMARY_FONT_COLOR={
                                            palette.PRIMARY_FONT_COLOR
                                        }
                                        $isDarkMode={
                                            palette.isDarkMode
                                        }
                                        onClick={
                                            handleClose
                                        }
                                    >
                                        <FontAwesomeIcon
                                            icon={faTimes}
                                        />
                                    </Styled.CloseButton>
                                </Styled.ModalHeader>
                                <Styled.ModalBody
                                    $isDarkMode={
                                        palette.isDarkMode
                                    }
                                    $CARD_BORDER_COLOR={
                                        palette.CARD_BORDER_COLOR
                                    }
                                    $PRIMARY_FONT_COLOR={
                                        palette.PRIMARY_FONT_COLOR
                                    }
                                >
                                    <ul>
                                        {listData.map(
                                            (c, idx) => (
                                                <li
                                                    key={
                                                        c.id
                                                    }
                                                >
                                                    <span>
                                                        {
                                                            idx
                                                        }
                                                        .
                                                    </span>
                                                    <Flag
                                                        iso3={
                                                            c.country
                                                        }
                                                        size={
                                                            20
                                                        }
                                                    />
                                                    <strong>
                                                        {
                                                            c.label
                                                        }
                                                    </strong>
                                                    <span className="scarf-count">
                                                        {
                                                            c.value
                                                        }{' '}
                                                        {c.value ===
                                                        1
                                                            ? 'scarf'
                                                            : 'scarves'}
                                                    </span>
                                                </li>
                                            )
                                        )}
                                    </ul>
                                </Styled.ModalBody>
                                <Styled.ModalFooter
                                    $MODAL_BACKGROUND_COLOR={
                                        palette.MODAL_BACKGROUND_COLOR
                                    }
                                    $CARD_BORDER_COLOR={
                                        palette.CARD_BORDER_COLOR
                                    }
                                >
                                    <Styled.ShowAllButton
                                        onClick={
                                            handleClose
                                        }
                                        $CARD_BACKGROUND_COLOR={
                                            palette.CARD_BACKGROUND_COLOR
                                        }
                                        $PRIMARY_FONT_COLOR={
                                            palette.PRIMARY_FONT_COLOR
                                        }
                                        $CARD_BORDER_COLOR={
                                            palette.CARD_BORDER_COLOR
                                        }
                                        $isDarkMode={
                                            palette.isDarkMode
                                        }
                                        style={{
                                            width: '100%',
                                        }}
                                    >
                                        Close
                                    </Styled.ShowAllButton>
                                </Styled.ModalFooter>
                            </Styled.ModalContent>
                        </Modal>
                    </Styled.ContributorsLeft>

                    <Styled.ContributorsRight>
                        <Styled.ChartCard>
                            <ResponsivePie
                                data={pieData}
                                sortByValue={false}
                                startAngle={0}
                                margin={{
                                    top: 10,
                                    right: 10,
                                    bottom: 10,
                                    left: 10,
                                }}
                                innerRadius={0.7}
                                padAngle={2}
                                cornerRadius={8}
                                activeOuterRadiusOffset={8}
                                colors={({ id }) =>
                                    stringToColor(
                                        id,
                                        isDarkMode
                                    )
                                }
                                borderWidth={1}
                                borderColor={
                                    CARD_BORDER_COLOR
                                }
                                enableArcLabels={false}
                                enableArcLinkLabels={false}
                                defs={pieData.map(d => ({
                                    id: `gradient-${d.id}`,
                                    type: 'linearGradient',
                                    colors: [
                                        {
                                            offset: 0,
                                            color: stringToColor(
                                                d.id,
                                                isDarkMode
                                            ),
                                            opacity: 0.8,
                                        },
                                        {
                                            offset: 100,
                                            color: stringToColor(
                                                d.id,
                                                isDarkMode
                                            ),
                                            opacity: 0.3,
                                        },
                                    ],
                                }))}
                                fill={pieData.map(d => ({
                                    match: {
                                        id: d.id,
                                    },
                                    id: `gradient-${d.id}`,
                                }))}
                                theme={{
                                    tooltip: {
                                        container: {
                                            background:
                                                PRIMARY_BG_COLOR,
                                            color: PRIMARY_FONT_COLOR,
                                            borderRadius:
                                                '12px',
                                            border: `1px solid ${CARD_BORDER_COLOR}`,
                                            backdropFilter:
                                                'blur(10px)',
                                            fontSize:
                                                '14px',
                                        },
                                    },
                                }}
                                tooltip={({ datum }) => (
                                    <div
                                        style={{
                                            padding:
                                                '8px 12px',
                                            background:
                                                PRIMARY_BG_COLOR,
                                            color: PRIMARY_FONT_COLOR,
                                            borderRadius:
                                                '12px',
                                            border: `1px solid ${CARD_BORDER_COLOR}`,
                                            backdropFilter:
                                                'blur(10px)',
                                            fontSize:
                                                '14px',
                                        }}
                                    >
                                        <strong>
                                            {
                                                datum.data
                                                    .label
                                            }
                                        </strong>
                                        : {datum.value}
                                    </div>
                                )}
                                layers={[
                                    'arcs',
                                    ({
                                        centerX,
                                        centerY,
                                    }) => (
                                        <text
                                            x={centerX}
                                            y={centerY}
                                            textAnchor="middle"
                                            dominantBaseline="central"
                                            style={{
                                                fontSize: 64,
                                                fontWeight:
                                                    'bold',
                                                fill: PRIMARY_FONT_COLOR,
                                            }}
                                        >
                                            <tspan
                                                x={centerX}
                                                dy="-0.1em"
                                            >
                                                {
                                                    totalScarves
                                                }
                                            </tspan>
                                            <tspan
                                                x={centerX}
                                                dy="1.5em"
                                                fontSize="24"
                                                style={{
                                                    opacity: 0.7,
                                                    fontWeight: 400,
                                                    letterSpacing: 4,
                                                }}
                                            >
                                                scarves
                                            </tspan>
                                        </text>
                                    ),
                                ]}
                            />
                        </Styled.ChartCard>
                    </Styled.ContributorsRight>
                </Styled.ContributorsSection>
            </Styled.HeaderContent>
        </Styled.HeaderWrapper>
    );
};

export default Header;
